import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Sparkles, FileText, Loader2 } from 'lucide-react';

import MinistryReportModal from '../components/MinistryReportModal';
import { DEFAULT_THEME } from '../constants/theme';

import StatisticsAiBriefingModal from './statistics/StatisticsAiBriefingModal';
import StatisticsInfographicHero from './statistics/StatisticsInfographicHero';

import StatisticsDesktopAttendanceTrendCard from './statistics/StatisticsDesktopAttendanceTrendCard';
import StatisticsDesktopFaithDemographicsCard from './statistics/StatisticsDesktopFaithDemographicsCard';
import StatisticsDesktopVisitTypesCard from './statistics/StatisticsDesktopVisitTypesCard';
import StatisticsDesktopNewcomerFunnelCard from './statistics/StatisticsDesktopNewcomerFunnelCard';
import StatisticsDesktopDemographicsCard from './statistics/StatisticsDesktopDemographicsCard';
import StatisticsDesktopFinanceFlowCard from './statistics/StatisticsDesktopFinanceFlowCard';
import StatisticsDesktopTeacherEngagementCard from './statistics/StatisticsDesktopTeacherEngagementCard';
import StatisticsDesktopVisitationHeatmapCard from './statistics/StatisticsDesktopVisitationHeatmapCard';
import StatisticsDesktopAttendanceBreakdownCard from './statistics/StatisticsDesktopAttendanceBreakdownCard';
import StatisticsDesktopGradeTreemapCard from './statistics/StatisticsDesktopGradeTreemapCard';
import StatisticsDesktopCapabilityRadialCard from './statistics/StatisticsDesktopCapabilityRadialCard';
import StatisticsDesktopRecentVisitsCard from './statistics/StatisticsDesktopRecentVisitsCard'; // [NEW] 심방 내역 카드
import StatisticsGlassCard from './statistics/StatisticsGlassCard';

import StudentDetailModal from './student/StudentDetailModal';
import { FAITH_STATE_STYLES, ICONS as STUDENT_ICONS } from './student/constants';
import useDraggableModal from './student/useDraggableModal';
import { STATS_ICONS } from './statistics/statisticsAssets';
import { STATS_PERIOD_OPTIONS } from './statistics/statisticsDefaults';

import useStatisticsAiBriefing from './statistics/useStatisticsAiBriefing';
import useCountUp from './statistics/useCountUp';
import useStatisticsDerivedData from './statistics/useStatisticsDerivedData';
import useStatisticsReportStats from './statistics/useStatisticsReportStats';

export default function StatisticsView(props) {
  const {
    students = [],
    teachers = [],
    attendanceLogs = [],
    finances = [],
    annualBudget = {},
    visitLogs = [],
    selectedDepartment,
    theme: userTheme,
    onOpenStudentDetail,
  } = props;

  const theme = userTheme || DEFAULT_THEME;
  const isAll = selectedDepartment === 'All';

  // [Local State for Reports & Preferences]
  const [isReportOpen, setIsReportOpen] = useState(false);
  const { aiResult, closeAiModal, handleAiBriefing, isAiLoading, isAiModalOpen, activeAnalysisType, analysisTypes } = useStatisticsAiBriefing({
    selectedDepartment,
    students,
    teachers,
    attendanceLogs,
    finances,
    visitLogs,
    schedules: []
  });
  const [statsPeriodKey, setStatsPeriodKey] = useState(STATS_PERIOD_OPTIONS[0].key);
  const [viewingStudent, setViewingStudent] = useState(null);

  const {
    pos: detailModalPos,
    setPos: setDetailModalPos,
    handleMouseDown: handleDetailMouseDown,
  } = useDraggableModal({
    blockedSelectors: ["button", "a"],
  });

  const {
    attendanceBreakdownStats,
    attendanceSparkline,
    attendanceStats,
    avgAttendance,
    capabilityRadar,
    demographicsStats,
    faithStats,
    filteredStudents,
    financeFlowStats,
    financeStats,
    gradeDetailedStats,
    gradeTreemapStats,
    latestDataDate,
    longTermAbsentees,
    newcomerFunnelStats,
    teacherEngagementStats,
    thisWeekAttendance,
    visitationHeatmapStats,
    visitationStats,
    visitTypeStats
  } = useStatisticsDerivedData({
    attendanceLogs,
    finances,
    annualBudget,
    isAll,
    selectedDepartment,
    students,
    teachers,
    visitLogs,
    periodKey: statsPeriodKey
  });

  // [Animations]
  const animatedStudentCount = useCountUp(filteredStudents.length);
  const animatedAttendanceRate = useCountUp(avgAttendance.current);
  const animatedVisitationRate = useCountUp(visitationStats.rate);

  // [Period label]
  const selectedPeriodLabel = useMemo(() => {
    return STATS_PERIOD_OPTIONS.find((option) => option.key === statsPeriodKey)?.label || STATS_PERIOD_OPTIONS[0].label;
  }, [statsPeriodKey]);

  const ministryReportStats = useStatisticsReportStats({
    avgAttendance,
    filteredStudents,
    financeStats,
    longTermAbsentees,
    visitationStats,
  });

  const handleShareStudentSummary = useCallback((student) => {
    if (!student || !navigator?.clipboard?.writeText) return;
    const shareText = `[학생 정보]\n이름: ${student.name || "-"}\n직분/부서: ${student.department || student.grade || "-"}\n신앙상태: ${student.faithState || "-"}`;
    navigator.clipboard.writeText(shareText).catch(() => { });
  }, []);

  const closeViewingStudent = useCallback(() => {
    setViewingStudent(null);
    setDetailModalPos({ x: 0, y: 0 });
  }, [setDetailModalPos]);

  return (
    <div id="tour-stats-main" className="min-h-screen bg-[#FDFCFB] text-gray-800 font-sans selection:bg-[#354F38]/20 selection:text-[#354F38] pb-24 md:pb-12 md:px-6">

      {/* Dynamic Header & Hero */}
      <div className="max-w-[1400px] mx-auto pt-6 px-4 md:px-0">
        <StatisticsInfographicHero
          animatedStudentCount={animatedStudentCount}
          animatedAttendanceRate={animatedAttendanceRate}
          animatedVisitationRate={animatedVisitationRate}
          attendanceSparkline={attendanceSparkline}
          avgAttendance={avgAttendance}
          capabilityRadar={capabilityRadar}
          chartIcon={STATS_ICONS.chart}
          faithStats={faithStats}
          financeStats={financeStats}
          gradeDetailedStats={gradeDetailedStats}
          isAiLoading={isAiLoading}
          isAll={isAll}
          latestDataDate={latestDataDate}
          onChangePeriodKey={setStatsPeriodKey}
          onOpenAiBriefing={handleAiBriefing}
          onOpenReport={() => setIsReportOpen(true)}
          periodOptions={STATS_PERIOD_OPTIONS}
          selectedDepartment={selectedDepartment}
          selectedPeriodKey={statsPeriodKey}
          selectedPeriodLabel={selectedPeriodLabel}
          theme={theme}
          thisWeekAttendance={thisWeekAttendance}
          visitationStats={visitationStats}
        />

        {/* The New Ultimate Grid Layout */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">

          {/* Row 1: Attendance Trends (Wide) & Capability */}
          <div className="md:col-span-2 xl:col-span-3 min-h-[340px] flex items-stretch">
            <StatisticsDesktopAttendanceTrendCard attendanceStats={attendanceStats} isAll={isAll} theme={theme} isEditMode={false} />
          </div>
          <div className="min-h-[340px] flex items-stretch">
            <StatisticsDesktopCapabilityRadialCard capabilityRadar={capabilityRadar} isEditMode={false} />
          </div>

          {/* Row 2: Deep Dive into Attendance & Finance */}
          <div className="md:col-span-2 min-h-[340px] flex items-stretch">
            <StatisticsDesktopAttendanceBreakdownCard attendanceBreakdownStats={attendanceBreakdownStats} isEditMode={false} />
          </div>
          <div className="md:col-span-1 xl:col-span-2 min-h-[340px] flex items-stretch">
            <StatisticsDesktopFinanceFlowCard financeFlowStats={financeFlowStats} theme={theme} isEditMode={false} />
          </div>

          {/* Row 3: Demographics across the board */}
          <div className="md:col-span-2 min-h-[340px] flex items-stretch">
            <StatisticsDesktopDemographicsCard demographicsStats={demographicsStats} theme={theme} isEditMode={false} />
          </div>
          <div className="md:col-span-1 min-h-[340px] flex items-stretch">
            <StatisticsDesktopFaithDemographicsCard faithStats={faithStats} filteredStudentsLength={filteredStudents.length} isEditMode={false} />
          </div>
          <div className="md:col-span-1 xl:col-span-1 min-h-[340px] flex items-stretch">
            <StatisticsDesktopNewcomerFunnelCard newcomerFunnelStats={newcomerFunnelStats} theme={theme} isEditMode={false} />
          </div>

          {/* Row 4: Visitation & Teachers */}
          <div className="md:col-span-2 min-h-[340px] flex items-stretch">
            <StatisticsDesktopVisitationHeatmapCard visitationHeatmapStats={visitationHeatmapStats} theme={theme} isEditMode={false} />
          </div>
          <div className="md:col-span-1 min-h-[340px] flex items-stretch">
            <StatisticsDesktopRecentVisitsCard visitLogs={visitLogs} students={students} />
          </div>
          <div className="md:col-span-1 xl:col-span-1 min-h-[340px] flex items-stretch">
            <StatisticsDesktopTeacherEngagementCard teacherEngagementStats={teacherEngagementStats} theme={theme} isEditMode={false} />
          </div>
          <div className="md:col-span-1 xl:col-span-1 min-h-[340px] flex items-stretch">
            <StatisticsDesktopVisitTypesCard visitTypeStats={visitTypeStats} isEditMode={false} />
          </div>

          {/* Row 5: Huge Treemap at the bottom */}
          <div className="md:col-span-2 lg:col-span-3 xl:col-span-4 min-h-[400px] flex items-stretch">
            <StatisticsDesktopGradeTreemapCard gradeTreemapStats={gradeTreemapStats} isEditMode={false} />
          </div>

        </div>
      </div>

      {/* Common Modals */}
      <MinistryReportModal
        isOpen={isReportOpen}
        onClose={() => setIsReportOpen(false)}
        deptName={isAll ? '전 부서' : selectedDepartment}
        theme={theme}
        stats={ministryReportStats}
        aiResult={aiResult}
      />
      <StatisticsAiBriefingModal
        aiResult={aiResult}
        isAiLoading={isAiLoading}
        isOpen={isAiModalOpen}
        onClose={closeAiModal}
        onRefresh={() => handleAiBriefing(true)}
        showRefresh
        activeAnalysisType={activeAnalysisType}
        onChangeAnalysisType={(type) => handleAiBriefing(false, type)}
      />
      <StudentDetailModal
        viewingStudent={viewingStudent}
        detailModalPos={detailModalPos}
        handleDetailMouseDown={handleDetailMouseDown}
        onClose={closeViewingStudent}
        visitLogs={visitLogs}
        attendanceLogs={attendanceLogs}
        icons={STUDENT_ICONS}
        theme={theme}
        faithStateStyles={FAITH_STATE_STYLES}
        onOpenLog={() => { }}
        isTeacher={true}
        onShare={handleShareStudentSummary}
        onEdit={() => { }}
        onDelete={() => { }}
      />
    </div>
  );
}
